import './sentry'; // Must come before anything else
import './gtag';
import * as Sentry from '@sentry/react';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { View } from 'react-native';
import { MenuProvider } from 'react-native-popup-menu';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { createAppRouter } from '~/App';
import { ErrorPage } from '~/pages/ErrorPage';
import { DEFAULT_FLAGS } from '~/featureFlags';
import { FlagsmithIdentityProvider } from '~/providers/flagsmith/FlagsmithIdentityProvider';
import { HttpClientProvider } from '~/HttpClient';
import { getEnvironmentVariables } from '~/environment';

async function initializeApp() {
  const rootElement = document.getElementById('app');

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  if (rootElement.innerHTML) {
    return; // Already initialized
  }

  await setupMockServiceWorker();
  renderApp(rootElement);
}

async function setupMockServiceWorker() {
  if (!enableMockServiceWorker()) {
    return;
  }

  try {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      // Do not log unhandled requests as it generates a lot of noise.
      onUnhandledRequest: 'bypass',
    });
  } catch (error) {
    console.error('Failed to initialize mock service worker:', error);
  }
}

function enableMockServiceWorker() {
  // Mock service worker cannot be enabled in production builds (dev, stage, prod).
  if (import.meta.env.PROD) {
    return false;
  }

  // Mock service worker must be explicitly enabled in development builds.
  if (!import.meta.env.VITE_MOCK_SERVICE_WORKER) {
    return false;
  }

  return true;
}

const renderApp = (container: HTMLElement) => {
  const { webClientId, flagsmithEnvironmentId } = getEnvironmentVariables();
  const queryClient = new QueryClient();
  const router = createAppRouter();
  const root = ReactDOM.createRoot(container);

  root.render(
    <GoogleOAuthProvider clientId={`${webClientId}`}>
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <HttpClientProvider>
            <FlagsmithProvider
              flagsmith={flagsmith}
              options={{ environmentID: flagsmithEnvironmentId, preventFetch: true, defaultFlags: DEFAULT_FLAGS }}>
              <FlagsmithIdentityProvider>
                <View style={{ height: '100%' }}>
                  <MenuProvider>
                    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
                      <RouterProvider router={router} />
                    </Sentry.ErrorBoundary>
                  </MenuProvider>
                </View>
              </FlagsmithIdentityProvider>
            </FlagsmithProvider>
          </HttpClientProvider>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </StrictMode>
    </GoogleOAuthProvider>,
  );
};

// Initialize the application
initializeApp().catch((error) => {
  console.error('Failed to initialize application:', error);
});
